import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES, CONFIRM_MESSAGES } from '../utils/toastMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const ExecutivesAdminPage = () => {
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [executiveToDelete, setExecutiveToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { success, error: showError } = useToast();
  
  // Client assignment state
  const [assignedClients, setAssignedClients] = useState([]);
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientToAssign, setSelectedClientToAssign] = useState('');
  const [assigningClient, setAssigningClient] = useState(false);
  const [unassigningClientId, setUnassigningClientId] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    title: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    bio: '',
    work_hours: 'Mon–Fri, 9am–5pm EST',
    preferred_contact_method: 'Email',
    timezone: 'EST'
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/all`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setExecutives(data.executives || []);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching executives:', err);
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setError('');
    
    if (!formData.full_name || !formData.title || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      
      if (data.ok) {
        setShowCreateModal(false);
        setFormData({
          full_name: '',
          title: '',
          email: '',
          phone: '',
          company: '',
          location: '',
          bio: '',
          work_hours: 'Mon–Fri, 9am–5pm EST',
          preferred_contact_method: 'Email',
          timezone: 'EST'
        });
        success('Executive created', 'New profile has been added successfully.');
        fetchData();
      } else {
        setError(data.message || 'Failed to create executive');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleUpdate = async () => {
    setError('');

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/${selectedExecutive.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      
      if (data.ok) {
        setShowEditModal(false);
        setSelectedExecutive(null);
        success('Executive updated', 'Profile has been updated successfully.');
        fetchData();
      } else {
        setError(data.message || 'Failed to update executive');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!executiveToDelete) return;
    
    setIsDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/${executiveToDelete.id}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': ADMIN_KEY }
      });

      if (res.ok) {
        success('Executive deleted', 'Profile has been removed from the system.');
        fetchData();
      } else {
        showError('Failed to delete', 'Please try again.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setExecutiveToDelete(null);
    }
  };

  const openEditModal = async (executive) => {
    setSelectedExecutive(executive);
    setFormData({
      full_name: executive.full_name,
      title: executive.title,
      email: executive.email,
      phone: executive.phone || '',
      company: executive.company || '',
      location: executive.location || '',
      bio: executive.bio || '',
      work_hours: executive.work_hours || 'Mon–Fri, 9am–5pm EST',
      preferred_contact_method: executive.preferred_contact_method || 'Email',
      timezone: executive.timezone || 'EST'
    });
    setError('');
    setShowEditModal(true);
    
    // Fetch clients for this executive
    await fetchClientsForExecutive(executive.id);
  };

  const fetchClientsForExecutive = async (executiveId) => {
    setLoadingClients(true);
    try {
      // Fetch assigned clients
      const assignedRes = await fetch(`${BACKEND_URL}/api/executives/${executiveId}/clients`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (assignedRes.ok) {
        const data = await assignedRes.json();
        setAssignedClients(data.clients || []);
      }

      // Fetch unassigned clients
      const unassignedRes = await fetch(`${BACKEND_URL}/api/clients/unassigned`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (unassignedRes.ok) {
        const data = await unassignedRes.json();
        setUnassignedClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAssignClient = async () => {
    if (!selectedClientToAssign || !selectedExecutive) return;
    
    setAssigningClient(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${selectedClientToAssign}/executive`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ executive_id: selectedExecutive.id })
      });

      const data = await res.json();
      if (data.ok) {
        success('Client assigned', `Client has been assigned to ${selectedExecutive.full_name}`);
        setSelectedClientToAssign('');
        await fetchClientsForExecutive(selectedExecutive.id);
      } else {
        showError('Assignment failed', data.message || 'Failed to assign client');
      }
    } catch (err) {
      console.error('Error assigning client:', err);
      showError('Network error', 'Please try again');
    } finally {
      setAssigningClient(false);
    }
  };

  const handleUnassignClient = async (clientId) => {
    if (!clientId) return;
    
    setUnassigningClientId(clientId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${clientId}/executive`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ executive_id: null })
      });

      const data = await res.json();
      if (data.ok) {
        success('Client unassigned', 'Client has been removed from this executive');
        await fetchClientsForExecutive(selectedExecutive.id);
      } else {
        showError('Failed to unassign', data.message || 'Please try again');
      }
    } catch (err) {
      console.error('Error unassigning client:', err);
      showError('Network error', 'Please try again');
    } finally {
      setUnassigningClientId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', background: '#F8F9FA' }}>
        <p style={{ color: '#6B7280' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Executive Management
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              Create and manage executive profiles
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => window.location.href = '/admin'}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: '#6B7280',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Back to Admin
            </button>
            <button
              onClick={() => {
                setFormData({
                  full_name: '',
                  title: '',
                  email: '',
                  phone: '',
                  company: '',
                  location: '',
                  bio: '',
                  work_hours: 'Mon–Fri, 9am–5pm EST',
                  preferred_contact_method: 'Email',
                  timezone: 'EST'
                });
                setError('');
                setShowCreateModal(true);
              }}
              style={{
                padding: '10px 16px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              + New Executive
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Executive</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Title</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Contact</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Work Hours</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {executives.map((executive, idx) => (
                <tr key={executive.id} style={{ borderBottom: idx < executives.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <td style={{ padding: '16px' }}>
                    <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: 0 }}>{executive.full_name}</p>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ color: '#374151', fontSize: '13px' }}>{executive.title}</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <p style={{ color: '#374151', fontSize: '13px', margin: '0 0 2px 0' }}>{executive.email}</p>
                      {executive.phone && (
                        <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>{executive.phone}</p>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ color: '#6B7280', fontSize: '13px' }}>{executive.work_hours || 'Not set'}</span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => openEditModal(executive)}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: '#6B7280'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setExecutiveToDelete(executive);
                          setShowDeleteConfirm(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          border: '1px solid #FCA5A5',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: '#DC2626'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {executives.length === 0 && (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>No executives yet. Create your first executive profile to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '1px solid #E5E7EB'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Create New Executive
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Full Name *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Smith"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="CEO, Founder, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Company (optional)</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Corp"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Location (optional)</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="New York, NY"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Bio (optional)</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief professional background..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Work Hours</label>
                <input
                  type="text"
                  value={formData.work_hours}
                  onChange={(e) => setFormData({ ...formData, work_hours: e.target.value })}
                  placeholder="Mon–Fri, 9am–5pm EST"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Preferred Contact</label>
                <select
                  value={formData.preferred_contact_method}
                  onChange={(e) => setFormData({ ...formData, preferred_contact_method: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="Email">Email</option>
                  <option value="Phone">Phone</option>
                  <option value="Text">Text</option>
                  <option value="Slack">Slack</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="EST">EST (Eastern)</option>
                  <option value="CST">CST (Central)</option>
                  <option value="MST">MST (Mountain)</option>
                  <option value="PST">PST (Pacific)</option>
                  <option value="GMT">GMT (Greenwich)</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Create Executive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedExecutive && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '1px solid #E5E7EB'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Edit Executive
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Company</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Work Hours</label>
                <input
                  type="text"
                  value={formData.work_hours}
                  onChange={(e) => setFormData({ ...formData, work_hours: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Preferred Contact</label>
                <select
                  value={formData.preferred_contact_method}
                  onChange={(e) => setFormData({ ...formData, preferred_contact_method: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="Email">Email</option>
                  <option value="Phone">Phone</option>
                  <option value="Text">Text</option>
                  <option value="Slack">Slack</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="EST">EST (Eastern)</option>
                  <option value="CST">CST (Central)</option>
                  <option value="MST">MST (Mountain)</option>
                  <option value="PST">PST (Pacific)</option>
                  <option value="GMT">GMT (Greenwich)</option>
                </select>
              </div>

              {/* Assigned Clients Section */}
              <div style={{ 
                marginTop: '8px',
                paddingTop: '20px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <label style={{ 
                  display: 'block', 
                  color: '#0A0A0A', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  marginBottom: '12px' 
                }}>
                  Assigned Clients
                </label>

                {/* Assign Client Dropdown */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <select
                    value={selectedClientToAssign}
                    onChange={(e) => setSelectedClientToAssign(e.target.value)}
                    disabled={loadingClients || unassignedClients.length === 0}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#FFFFFF'
                    }}
                  >
                    <option value="">
                      {loadingClients ? 'Loading...' : unassignedClients.length === 0 ? 'No unassigned clients' : 'Select a client to assign...'}
                    </option>
                    {unassignedClients.map(client => (
                      <option key={client.client_id} value={client.client_id}>
                        {client.full_name || client.business_name || client.client_id} {client.business_name && client.full_name ? `(${client.business_name})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssignClient}
                    disabled={!selectedClientToAssign || assigningClient}
                    style={{
                      padding: '10px 16px',
                      background: selectedClientToAssign ? '#10B981' : '#E5E7EB',
                      color: selectedClientToAssign ? '#FFFFFF' : '#9CA3AF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: selectedClientToAssign ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {assigningClient ? 'Assigning...' : 'Assign'}
                  </button>
                </div>

                {/* Assigned Clients List */}
                <div style={{ 
                  background: '#F9FAFB', 
                  borderRadius: '8px', 
                  border: '1px solid #E5E7EB',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {loadingClients ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                      Loading clients...
                    </div>
                  ) : assignedClients.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                      No clients assigned to this executive yet.
                    </div>
                  ) : (
                    assignedClients.map((client, idx) => (
                      <div
                        key={client.client_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          borderBottom: idx < assignedClients.length - 1 ? '1px solid #E5E7EB' : 'none'
                        }}
                      >
                        <div>
                          <p style={{ 
                            margin: 0, 
                            color: '#0A0A0A', 
                            fontSize: '13px', 
                            fontWeight: '500' 
                          }}>
                            {client.full_name || client.business_name || client.client_id}
                          </p>
                          {client.business_name && client.full_name && (
                            <p style={{ margin: '2px 0 0 0', color: '#6B7280', fontSize: '12px' }}>
                              {client.business_name}
                            </p>
                          )}
                          {client.executive_assigned_at && (
                            <p style={{ margin: '2px 0 0 0', color: '#9CA3AF', fontSize: '11px' }}>
                              Assigned: {new Date(client.executive_assigned_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnassignClient(client.client_id)}
                          disabled={unassigningClientId === client.client_id}
                          style={{
                            padding: '4px 10px',
                            background: 'transparent',
                            color: unassigningClientId === client.client_id ? '#9CA3AF' : '#DC2626',
                            border: '1px solid #FCA5A5',
                            borderRadius: '4px',
                            fontSize: '11px',
                            cursor: unassigningClientId === client.client_id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {unassigningClientId === client.client_id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Executive"
        message="Are you sure you want to delete this executive profile? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ExecutivesAdminPage;
